import {dataTestId} from '@hconnect/uikit'
import {Column, DataTable} from '@hconnect/uikit/src/lib2'
import {ChatBubbleOutline, Edit, MoreVert, MyLocation} from '@mui/icons-material'
import {Chip, IconButton, Stack, Tooltip, Typography} from '@mui/material'
import React, {useMemo} from 'react'
import {useTranslation} from 'react-i18next'

import {formatLabelWithUnit} from '../hooks/useStrengthMeasurementUnit'
import {StrengthTarget} from '../types'
import {canModifyTarget} from '../utils/dateUtils'

interface StrengthTargetListDesktopTableProps {
  targets: StrengthTarget[]
  unit: string | undefined
  isReadOnly: boolean
  timezone: string
  onEditTarget: (target: StrengthTarget) => void
  onMenuOpen: (event: React.MouseEvent<HTMLElement>, target: StrengthTarget) => void
  onCommentClick: (target: StrengthTarget) => void
  formatDate: (dateString: string) => string
  formatValidFrom: (dateString: string) => string
}

export const StrengthTargetListDesktopTable: React.FC<StrengthTargetListDesktopTableProps> = ({
  targets,
  unit,
  isReadOnly,
  timezone,
  onEditTarget,
  onMenuOpen,
  onCommentClick,
  formatDate,
  formatValidFrom
}) => {
  const {t} = useTranslation()

  const columns: Column<StrengthTarget>[] = useMemo(
    () => [
      {
        key: 'target',
        label: formatLabelWithUnit(t('materialsSettings.strengthTargets.target'), unit),
        customTemplate: (target) => (
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            {...dataTestId(`cell_target_${target.id}`)}
          >
            {target.isCurrent && (
              <MyLocation
                fontSize="small"
                sx={{
                  color: (theme) => theme.palette.primary.main
                }}
                {...dataTestId(`current_icon_${target.id}`)}
              />
            )}
            <Typography>{target.target ?? '-'}</Typography>
            {target.isCurrent && (
              <Chip
                label={t('materialsSettings.strengthTargets.current')}
                size="small"
                sx={{
                  backgroundColor: (theme) => theme.palette.info.light,
                  color: (theme) => theme.palette.info.contrastText,
                  fontSize: '12px',
                  height: 24
                }}
                {...dataTestId(`current_chip_${target.id}`)}
              />
            )}
          </Stack>
        ),
        headerElAdditionalProps: {id: 'header_target'}
      },
      {
        key: 'lowerBound',
        label: formatLabelWithUnit(t('materialsSettings.strengthTargets.lowerBound'), unit),
        customTemplate: (target) => (
          <span {...dataTestId(`cell_lower_bound_${target.id}`)}>{target.lowerBound ?? '-'}</span>
        ),
        headerElAdditionalProps: {id: 'header_lower_bound'}
      },
      {
        key: 'upperBound',
        label: formatLabelWithUnit(t('materialsSettings.strengthTargets.upperBound'), unit),
        customTemplate: (target) => (
          <span {...dataTestId(`cell_upper_bound_${target.id}`)}>{target.upperBound ?? '-'}</span>
        ),
        headerElAdditionalProps: {id: 'header_upper_bound'}
      },
      {
        key: 'validFrom',
        label: t('materialsSettings.strengthTargets.validFrom'),
        customTemplate: (target) => (
          <span {...dataTestId(`cell_valid_from_${target.id}`)}>
            {formatValidFrom(target.validFrom)}
          </span>
        )
      },
      {
        key: 'validTo',
        label: t('materialsSettings.strengthTargets.validTo'),
        customTemplate: (target) => (
          <span {...dataTestId(`cell_valid_to_${target.id}`)}>
            {target.validTo ? formatDate(target.validTo) : '-'}
          </span>
        ),
        headerElAdditionalProps: {id: 'header_valid_to'}
      },
      {
        key: 'comment',
        label: '',
        customTemplate: (target) => (
          <span {...dataTestId(`cell_comment_${target.id}`)}>
            {target.comment && (
              <Tooltip
                title={target.comment}
                arrow
                placement="top"
                enterDelay={500}
                {...dataTestId(`target_comment_tooltip_${target.id}`)}
              >
                <IconButton
                  size="small"
                  onClick={() => onCommentClick(target)}
                  {...dataTestId(`target_comment_icon_${target.id}`)}
                >
                  <ChatBubbleOutline fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </span>
        ),
        headerElAdditionalProps: {id: 'header_comment'}
      },
      {
        key: 'actions',
        label: '',
        align: 'right' as const,
        customTemplate: (target) => (
          <span {...dataTestId(`cell_actions_${target.id}`)}>
            {!isReadOnly && canModifyTarget(target.id, target.validFrom, timezone) && (
              <>
                {target.isCurrent ? (
                  <IconButton
                    size="small"
                    onClick={() => onEditTarget(target)}
                    {...dataTestId(`target_edit_button_${target.id}`)}
                  >
                    <Edit sx={{color: (theme) => theme.palette.primary.main}} />
                  </IconButton>
                ) : (
                  <IconButton
                    size="small"
                    onClick={(e) => onMenuOpen(e, target)}
                    {...dataTestId(`target_menu_button_${target.id}`)}
                  >
                    <MoreVert />
                  </IconButton>
                )}
              </>
            )}
          </span>
        ),
        headerElAdditionalProps: {id: 'header_actions'}
      }
    ],
    [
      t,
      unit,
      isReadOnly,
      timezone,
      onEditTarget,
      onMenuOpen,
      onCommentClick,
      formatDate,
      formatValidFrom
    ]
  )

  return (
    <DataTable
      columns={columns}
      data={targets}
      keyExtractor={(target) => target.id}
      rowSx={() => ({
        '&:hover': {
          backgroundColor: (theme) => theme.palette.action.hover
        }
      })}
      tableContainerSx={{
        '& .MuiTableHead-root .MuiTableRow-root': {
          backgroundColor: (theme) => theme.palette.grey[50]
        },
        '& .MuiTableHead-root .MuiTableCell-root': {
          fontWeight: 600
        }
      }}
      data-test-id="strength_targets_table"
    />
  )
}
